import React from 'react';
import { SectionHeader } from '../../primitives/SectionHeader';
import { PricingCard } from '../../cards/PricingCard';
import { colors, fonts, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';
import { InlineEditableText } from '../../../InlineEditableText';

// Interner Rechenfaktor für den Sanierungswert. Bewusst NICHT im Angebot ausgewiesen —
// der Kunde sieht nur den Gesamtwert der Sanierung, nicht die Herleitung.
const SANIERUNG_EUR_PRO_KWP = 230;

/**
 * Wandelt einen kWp-Wert (String oder Zahl) robust in eine Zahl um.
 * Toleriert deutsche ("1.234,5") und englische ("1234.5") Schreibweise.
 * Liefert NaN, wenn kein sinnvoller Wert erkennbar ist.
 */
const parseKwpValue = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  if (raw === null || raw === undefined) return NaN;

  const cleaned = String(raw).replace(/[^0-9.,-]/g, '').trim();
  if (!cleaned) return NaN;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  let normalized = cleaned;
  if (hasComma && hasDot) {
    // Letztes Trennzeichen ist das Dezimaltrennzeichen.
    normalized = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (hasComma) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasDot && /^-?\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    // Reiner Tausenderpunkt, z. B. "1.250"
    normalized = cleaned.replace(/\./g, '');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

/**
 * PricingSection – "Ihre Preisoptionen" auf Page 3.
 * Enthält Eyebrow + Headline + OfferMetaBar + PricingGrid.
 * Standard (Verpachtung): 2 identische PricingCards nebeneinander.
 * Variante "sanierung": eine einzelne Karte über die volle Breite.
 */
export const PricingSection = ({
  kundenname,
  angebotsnummer,
  einmalig,
  jaehrlich,
  gesamt,
  laufzeit,
  vlProzent,
  offerVariant = '',
  kwp = '',
  content = {},
  onTextChange,
  editMode = false,
  layoutEditLevel = 'section',
  dragProps = {},
}) => {
  const isSanierung = String(offerVariant).toLowerCase() === 'sanierung';
  const pricingText = {
    header: {
      eyebrow: content?.header?.eyebrow || 'Persönliches Angebot',
      headline: content?.header?.headline || 'IHRE PREISOPTIONEN',
    },
    meta: {
      auftraggeberLabel: content?.meta?.auftraggeberLabel || 'Auftraggeber',
      angebotsnummerLabel: content?.meta?.angebotsnummerLabel || 'Angebots-Nr.',
      angebotstypLabel: content?.meta?.angebotstypLabel || 'Angebotstyp',
      angebotstypValue: content?.meta?.angebotstypValue || 'Pachtzahlung - indikativ',
    },
    option1: {
      headerLabel: content?.option1?.headerLabel || 'Option 1 - Einmalzahlung',
      headerSub: content?.option1?.headerSub || 'Einmalige Auszahlung bei Vertragsabschluss',
      priceLabel: content?.option1?.priceLabel || 'Einmalige Auszahlung',
      priceFootnote: content?.option1?.priceFootnote || 'netto zzgl. MwSt.',
      rows: {
        auszahlung: content?.option1?.rows?.auszahlung || 'Auszahlung',
        laufzeit: content?.option1?.rows?.laufzeit || 'Laufzeit',
        nachVerlaengerung: content?.option1?.rows?.nachVerlaengerung || 'Nach Verlängerung',
        anlageNachLaufzeit: content?.option1?.rows?.anlageNachLaufzeit || 'Anlage nach Laufzeit',
        kostenFuerSie: content?.option1?.rows?.kostenFuerSie || 'Kosten für Sie',
      },
    },
    option2: {
      headerLabel: content?.option2?.headerLabel || 'Option 2 - Jährlich',
      headerSub: content?.option2?.headerSub || 'Jährliche Pacht über die gesamte Laufzeit',
      priceLabel: content?.option2?.priceLabel || 'Jährliche Auszahlung',
      priceFootnote: content?.option2?.priceFootnote || 'netto zzgl. MwSt. / Jahr',
      rows: {
        gesamtePachtzahlung: content?.option2?.rows?.gesamtePachtzahlung || 'Gesamte Pachtzahlung',
        laufzeit: content?.option2?.rows?.laufzeit || 'Laufzeit',
        nachVerlaengerung: content?.option2?.rows?.nachVerlaengerung || 'Nach Verlängerung',
        anlageNachLaufzeit: content?.option2?.rows?.anlageNachLaufzeit || 'Anlage nach Laufzeit',
        kostenFuerSie: content?.option2?.rows?.kostenFuerSie || 'Kosten für Sie',
      },
    },
    sanierung: {
      headerLabel: content?.sanierung?.headerLabel || 'Dachsanierung',
      headerSub: content?.sanierung?.headerSub || 'Sanierung Ihres Daches als Gegenleistung für die Dachnutzung',
      priceLabel: content?.sanierung?.priceLabel || 'Wert der Sanierung',
      priceFootnote: content?.sanierung?.priceFootnote || 'netto zzgl. MwSt.',
      rows: {
        leistung: content?.sanierung?.rows?.leistung || 'Leistungsumfang',
        zeitpunkt: content?.sanierung?.rows?.zeitpunkt || 'Zeitpunkt',
        laufzeit: content?.sanierung?.rows?.laufzeit || 'Laufzeit',
        nachVerlaengerung: content?.sanierung?.rows?.nachVerlaengerung || 'Nach Verlängerung',
        anlageNachLaufzeit: content?.sanierung?.rows?.anlageNachLaufzeit || 'Anlage nach Laufzeit',
        kostenFuerSie: content?.sanierung?.rows?.kostenFuerSie || 'Kosten für Sie',
      },
      values: {
        leistung: content?.sanierung?.values?.leistung || 'Sanierung der Dachhaut inkl. Montagevorbereitung',
        zeitpunkt: content?.sanierung?.values?.zeitpunkt || 'Vor dem Bau der PV-Anlage',
      },
    },
    values: {
      auszahlungVoraus: content?.values?.auszahlungVoraus || 'für 20 Jahre im Voraus',
      laufzeitSuffix: content?.values?.laufzeitSuffix || 'Jahre + Opt. 2x5 J.',
      nachVerlaengerungPrefix: content?.values?.nachVerlaengerungPrefix || 'Ertragsbeteiligung',
      anlageNachLaufzeit: content?.values?.anlageNachLaufzeit || 'Kostenfreie Übernahme möglich',
      kostenFuerSie: content?.values?.kostenFuerSie || 'Null €',
    },
    notice:
      content?.notice ||
      'Alle Angaben sind indikativ und vorbehaltlich einer Vor-Ort-Besichtigung, eines exakten Aufmaßes und der Netzrückmeldung. EN:POWER trägt alle Kosten für Planung, Installation, Betrieb und Versicherung der Anlage.',
  };

  const baseRows = [
    { label: pricingText.option1.rows.auszahlung, value: pricingText.values.auszahlungVoraus },
    { label: pricingText.option1.rows.laufzeit, value: `${laufzeit} ${pricingText.values.laufzeitSuffix}` },
    { label: pricingText.option1.rows.nachVerlaengerung, value: `${pricingText.values.nachVerlaengerungPrefix} ${vlProzent}%` },
    { label: pricingText.option1.rows.anlageNachLaufzeit, value: pricingText.values.anlageNachLaufzeit },
    { label: pricingText.option1.rows.kostenFuerSie, value: pricingText.values.kostenFuerSie, valueStyle: { color: colors.green, fontWeight: 600 } },
  ];

  const rowsOption2 = [
    { label: pricingText.option2.rows.gesamtePachtzahlung, value: `${gesamt},-€ netto`, valueStyle: { fontWeight: 700, color: colors.navy } },
    { label: pricingText.option2.rows.laufzeit, value: `${laufzeit} ${pricingText.values.laufzeitSuffix}` },
    { label: pricingText.option2.rows.nachVerlaengerung, value: `${pricingText.values.nachVerlaengerungPrefix} ${vlProzent}%` },
    { label: pricingText.option2.rows.anlageNachLaufzeit, value: pricingText.values.anlageNachLaufzeit },
    { label: pricingText.option2.rows.kostenFuerSie, value: pricingText.values.kostenFuerSie, valueStyle: { color: colors.green, fontWeight: 600 } },
  ];

  // Sanierungswert = kWp x 200 EUR, deutsch formatiert ohne Nachkommastellen (185 -> "37.000").
  const kwpNumber = isSanierung ? parseKwpValue(kwp) : NaN;
  const sanierungswert = Number.isFinite(kwpNumber) && kwpNumber > 0
    ? Math.round(kwpNumber * SANIERUNG_EUR_PRO_KWP).toLocaleString('de-DE', { maximumFractionDigits: 0 })
    : '–';

  const sanierungRows = isSanierung
    ? [
        { label: pricingText.sanierung.rows.leistung, value: pricingText.sanierung.values.leistung },
        { label: pricingText.sanierung.rows.zeitpunkt, value: pricingText.sanierung.values.zeitpunkt },
        { label: pricingText.sanierung.rows.laufzeit, value: `${laufzeit} ${pricingText.values.laufzeitSuffix}` },
        { label: pricingText.sanierung.rows.nachVerlaengerung, value: `${pricingText.values.nachVerlaengerungPrefix} ${vlProzent}%` },
        { label: pricingText.sanierung.rows.anlageNachLaufzeit, value: pricingText.values.anlageNachLaufzeit },
        { label: pricingText.sanierung.rows.kostenFuerSie, value: pricingText.values.kostenFuerSie, valueStyle: { color: colors.green, fontWeight: 600 } },
      ]
    : [];

  const metaItems = [
    { l: pricingText.meta.auftraggeberLabel, v: kundenname, c: colors.navy },
    { l: pricingText.meta.angebotsnummerLabel, v: angebotsnummer, c: colors.navy },
    { l: pricingText.meta.angebotstypLabel, v: pricingText.meta.angebotstypValue, c: colors.orange },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, background: 'white', minHeight: 0 }}>
      <div className="section-divider" />

      {layoutEditLevel === 'detail' ? (
        <DraggableElement id="S3-Preisoptionen-Header" {...dragProps} dragAxis="y">
          <div style={{ padding: `${space.md + space.xs}px ${space.xxl}px 0`, background: 'white', flexShrink: 0 }}>
            <SectionHeader
              eyebrow={<InlineEditableText as="span" value={pricingText.header.eyebrow} editable={editMode} onChange={(value) => onTextChange?.('pricing.header.eyebrow', value)} />}
              headline={<InlineEditableText as="span" value={pricingText.header.headline} editable={editMode} onChange={(value) => onTextChange?.('pricing.header.headline', value)} />}
            />
          </div>
        </DraggableElement>
      ) : (
        <div style={{ padding: `${space.md + space.xs}px ${space.xxl}px 0`, background: 'white', flexShrink: 0 }}>
          <SectionHeader
            eyebrow={<InlineEditableText as="span" value={pricingText.header.eyebrow} editable={editMode} onChange={(value) => onTextChange?.('pricing.header.eyebrow', value)} />}
            headline={<InlineEditableText as="span" value={pricingText.header.headline} editable={editMode} onChange={(value) => onTextChange?.('pricing.header.headline', value)} />}
          />
        </div>
      )}

      {layoutEditLevel === 'detail' ? (
        <DraggableElement id="S3-Preisoptionen-Meta" {...dragProps} dragAxis="y">
          <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, background: 'white', flexShrink: 0 }}>
            <div
              style={{
                background: colors.bgSoft,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '10px 16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: `${space.md}px`,
              }}
            >
              {metaItems.map((item, i) => (
                <div key={i}>
                  <p style={{ fontFamily: fonts.body, fontSize: '7.5px', fontWeight: 700, color: colors.slateLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>
                    {i === 0 && <InlineEditableText as="span" value={item.l} editable={editMode} onChange={(value) => onTextChange?.('pricing.meta.auftraggeberLabel', value)} />}
                    {i === 1 && <InlineEditableText as="span" value={item.l} editable={editMode} onChange={(value) => onTextChange?.('pricing.meta.angebotsnummerLabel', value)} />}
                    {i === 2 && <InlineEditableText as="span" value={item.l} editable={editMode} onChange={(value) => onTextChange?.('pricing.meta.angebotstypLabel', value)} />}
                  </p>
                  <p style={{ fontFamily: fonts.body, fontSize: '11px', fontWeight: 700, color: item.c }}>{item.v}</p>
                </div>
              ))}
            </div>
          </div>
        </DraggableElement>
      ) : (
        <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, background: 'white', flexShrink: 0 }}>
          <div
            style={{
              background: colors.bgSoft,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '10px 16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: `${space.md}px`,
            }}
          >
            {metaItems.map((item, i) => (
              <div key={i}>
                <p style={{ fontFamily: fonts.body, fontSize: '7.5px', fontWeight: 700, color: colors.slateLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>{item.l}</p>
                <p style={{ fontFamily: fonts.body, fontSize: '11px', fontWeight: 700, color: item.c }}>{item.v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isSanierung ? '1fr' : '1fr 1fr', gap: `${space.sm + space.xs}px` }}>
          {isSanierung ? (
            /* Sanierung: nur EINE Karte über die volle Breite – Drag-ID S3-Preisoption-1 bleibt erhalten. */
            layoutEditLevel === 'detail' ? (
              <DraggableElement id="S3-Preisoption-1" {...dragProps} dragAxis="y">
                <PricingCard
                  headerBg={colors.navy}
                  headerLabel={pricingText.sanierung.headerLabel}
                  headerSub={pricingText.sanierung.headerSub}
                  priceLabel={pricingText.sanierung.priceLabel}
                  priceValue={sanierungswert}
                  priceFootnote={pricingText.sanierung.priceFootnote}
                  priceColor={colors.orange}
                  rows={sanierungRows}
                />
              </DraggableElement>
            ) : (
              <PricingCard
                headerBg={colors.navy}
                headerLabel={pricingText.sanierung.headerLabel}
                headerSub={pricingText.sanierung.headerSub}
                priceLabel={pricingText.sanierung.priceLabel}
                priceValue={sanierungswert}
                priceFootnote={pricingText.sanierung.priceFootnote}
                priceColor={colors.orange}
                rows={sanierungRows}
              />
            )
          ) : (
            <>
              {layoutEditLevel === 'detail' ? (
                <DraggableElement id="S3-Preisoption-1" {...dragProps} dragAxis="y">
                  <PricingCard
                    headerBg={colors.navy}
                    headerLabel={pricingText.option1.headerLabel}
                    headerSub={pricingText.option1.headerSub}
                    priceLabel={pricingText.option1.priceLabel}
                    priceValue={einmalig}
                    priceFootnote={pricingText.option1.priceFootnote}
                    priceColor={colors.orange}
                    rows={baseRows}
                  />
                </DraggableElement>
              ) : (
                <PricingCard
                  headerBg={colors.navy}
                  headerLabel={pricingText.option1.headerLabel}
                  headerSub={pricingText.option1.headerSub}
                  priceLabel={pricingText.option1.priceLabel}
                  priceValue={einmalig}
                  priceFootnote={pricingText.option1.priceFootnote}
                  priceColor={colors.orange}
                  rows={baseRows}
                />
              )}

              {layoutEditLevel === 'detail' ? (
                <DraggableElement id="S3-Preisoption-2" {...dragProps} dragAxis="y">
                  <PricingCard
                    headerBg={colors.navy}
                    headerLabel={pricingText.option2.headerLabel}
                    headerSub={pricingText.option2.headerSub}
                    priceLabel={pricingText.option2.priceLabel}
                    priceValue={jaehrlich}
                    priceFootnote={pricingText.option2.priceFootnote}
                    priceColor={colors.navy}
                    rows={rowsOption2}
                  />
                </DraggableElement>
              ) : (
                <PricingCard
                  headerBg={colors.navy}
                  headerLabel={pricingText.option2.headerLabel}
                  headerSub={pricingText.option2.headerSub}
                  priceLabel={pricingText.option2.priceLabel}
                  priceValue={jaehrlich}
                  priceFootnote={pricingText.option2.priceFootnote}
                  priceColor={colors.navy}
                  rows={rowsOption2}
                />
              )}
            </>
          )}
        </div>

        {layoutEditLevel === 'detail' ? (
          <DraggableElement id="S3-Preisoptionen-Hinweis" {...dragProps} dragAxis="y" initialStyle={{ marginTop: 'auto', paddingTop: `${space.xl}px`, flexShrink: 0 }}>
            <div
              style={{
                padding: '9px 14px',
                background: colors.amberBg,
                border: `1px solid ${colors.amberBorder}`,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '11px', lineHeight: 1, marginTop: '1px', flexShrink: 0 }}>&#9432;</span>
              <p style={{ fontFamily: fonts.body, fontSize: '8.5px', color: colors.amberText, lineHeight: 1.5, margin: 0 }}>
                {pricingText.notice}
              </p>
            </div>
          </DraggableElement>
        ) : (
          <div style={{ marginTop: 'auto', paddingTop: `${space.xl}px`, flexShrink: 0 }}>
            <div
              style={{
                padding: '9px 14px',
                background: colors.amberBg,
                border: `1px solid ${colors.amberBorder}`,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '11px', lineHeight: 1, marginTop: '1px', flexShrink: 0 }}>&#9432;</span>
              <p style={{ fontFamily: fonts.body, fontSize: '8.5px', color: colors.amberText, lineHeight: 1.5, margin: 0 }}>{pricingText.notice}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
